import { useState, useEffect } from 'react';
import { mcqAPI, responseAPI } from '../../utils/api';
import {
  RiArrowLeftLine, RiQuestionLine, RiUserLine,
  RiCheckLine, RiCloseLine, RiBarChartLine,
} from 'react-icons/ri';

const TeacherResponses = () => {
  const [mcqs, setMcqs]                         = useState([]);
  const [selectedMcq, setSelectedMcq]           = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [responses, setResponses]               = useState([]);
  const [statistics, setStatistics]             = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [mcqData, setMcqData]                   = useState(null);

  useEffect(() => { fetchMCQs(); }, []);

  const fetchMCQs = async () => {
    try {
      const res = await mcqAPI.getAllMCQs();
      setMcqs(res.data.mcqs?.filter(m => m.isPublished) || []);
    } catch {}
    finally { setLoading(false); }
  };

  const handleMCQSelect = async (mcqId) => {
    setSelectedMcq(mcqId);
    setLoading(true);
    try {
      const [rRes, sRes, mRes] = await Promise.all([
        responseAPI.getResponsesByMCQ(mcqId),
        responseAPI.getMCQStatistics(mcqId),
        mcqAPI.getMCQ(mcqId),
      ]);
      setResponses(rRes.data.responses || []);
      setStatistics(sRes.data.statistics);
      setMcqData(mRes.data.mcq);
    } catch {}
    finally { setLoading(false); }
  };

  const resetToList = () => {
    setSelectedMcq(null);
    setSelectedResponse(null);
    setResponses([]);
    setStatistics(null);
    setMcqData(null);
  };

  /* ── Detail view ──────────────────────────────────────────── */
  if (selectedResponse && mcqData) {
    return (
      <div className="p-6 max-w-4xl mx-auto animate-fade-in-up">
        <button onClick={() => setSelectedResponse(null)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium mb-5 transition-colors">
          <RiArrowLeftLine className="w-4 h-4" /> Back to Responses
        </button>

        {/* Student summary */}
        <div className="card p-5 mb-5">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-800">{selectedResponse.studentName}</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Roll No: <span className="font-semibold text-slate-700">{selectedResponse.rollNumber}</span>
                &nbsp;·&nbsp;
                Class: <span className="font-semibold text-slate-700">{selectedResponse.className}</span>
              </p>
            </div>
            <span className={`badge text-white ${selectedResponse.passed ? 'bg-emerald-600' : 'bg-red-500'}`}
              style={{ padding: '6px 12px', fontSize: '0.75rem' }}>
              {selectedResponse.passed ? 'Passed' : 'Failed'}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Score',     value: `${selectedResponse.score}%`,  bg: 'bg-blue-50',   text: 'text-blue-600' },
              { label: 'Correct',   value: `${selectedResponse.correctAnswers}/${selectedResponse.totalQuestions}`, bg: 'bg-emerald-50', text: 'text-emerald-600' },
              { label: 'Time',      value: `${Math.floor(selectedResponse.timeTaken / 60)}m`, bg: 'bg-amber-50',   text: 'text-amber-600' },
              { label: 'Submitted', value: new Date(selectedResponse.submittedAt).toLocaleDateString(), bg: 'bg-violet-50',  text: 'text-violet-600' },
            ].map(({ label, value, bg, text }) => (
              <div key={label} className={`${bg} rounded-xl p-3 text-center border border-slate-100`}>
                <p className={`text-xl font-bold ${text}`}>{value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Question analysis */}
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Question Analysis</h2>
        <div className="space-y-3">
          {mcqData.questions.map((question) => {
            const sr      = selectedResponse.responses.find(r => r.questionNumber === question.questionNumber);
            const correct = sr?.isCorrect;
            return (
              <div key={question.questionNumber}
                className={`card p-4 border-l-4 ${correct ? 'border-emerald-500' : 'border-red-400'}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-2.5">
                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${correct ? 'bg-emerald-600' : 'bg-red-500'}`}>
                      {question.questionNumber}
                    </span>
                    <p className="font-medium text-slate-800 text-sm leading-snug">{question.questionText}</p>
                  </div>
                  <span className={`flex-shrink-0 badge ${correct ? 'badge-green' : 'badge-red'}`}>
                    {correct ? 'Correct' : 'Wrong'}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-8">
                  {question.options.map((opt) => {
                    const isStudent = opt.optionLetter === sr?.selectedAnswer;
                    const isCorrect = opt.optionLetter === question.correctAnswer;
                    return (
                      <div key={opt.optionLetter}
                        className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-sm ${
                          isCorrect ? 'border-emerald-400 bg-emerald-50'
                          : isStudent && !correct ? 'border-red-400 bg-red-50'
                          : 'border-slate-100 bg-white'
                        }`}>
                        <span className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                          isCorrect ? 'bg-emerald-600 text-white'
                          : isStudent && !correct ? 'bg-red-500 text-white'
                          : 'bg-slate-200 text-slate-600'
                        }`}>
                          {opt.optionLetter}
                        </span>
                        <p className="flex-1 text-slate-700 text-xs">{opt.optionText}</p>
                        {isCorrect && <RiCheckLine className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />}
                        {isStudent && !correct && <RiCloseLine className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── MCQ list ─────────────────────────────────────────────── */
  if (!selectedMcq) {
    return (
      <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800">Student Responses</h1>
          <p className="text-sm text-slate-500 mt-0.5">Select a published test to view results</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14">
            <div className="w-8 h-8 spinner" />
          </div>
        ) : mcqs.length === 0 ? (
          <div className="card p-12 text-center">
            <RiQuestionLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No published tests yet</p>
            <p className="text-slate-400 text-sm mt-1">Publish an MCQ test to start collecting responses</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
            {mcqs.map(mcq => (
              <button key={mcq._id} onClick={() => handleMCQSelect(mcq._id)}
                className="card-hover p-5 text-left group animate-fade-in-up">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <RiBarChartLine className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="badge badge-green">Published</span>
                </div>
                <h3 className="font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors mb-1 line-clamp-1">
                  {mcq.title}
                </h3>
                {mcq.description && (
                  <p className="text-sm text-slate-500 line-clamp-2 mb-3">{mcq.description}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-slate-400 pt-3 border-t border-slate-100">
                  <span>{mcq.totalQuestions} questions</span>
                  <span>{mcq.duration} min</span>
                  <span className="ml-auto text-emerald-600 font-semibold group-hover:translate-x-0.5 transition-transform inline-block">
                    View →
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Responses table ──────────────────────────────────────── */
  const mcq = mcqs.find(m => m._id === selectedMcq);

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
      <button onClick={resetToList}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium mb-5 transition-colors">
        <RiArrowLeftLine className="w-4 h-4" /> Back to Tests
      </button>

      <div className="mb-5">
        <h1 className="text-xl font-bold text-slate-800">{mcq?.title}</h1>
        <p className="text-sm text-slate-500 mt-0.5">Student submission results</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-14">
          <div className="w-8 h-8 spinner" />
        </div>
      ) : (
        <>
          {statistics && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              {[
                { label: 'Total Students', value: statistics.totalStudents,    color: 'text-blue-600',    bg: 'bg-blue-50' },
                { label: 'Average Score',  value: `${statistics.averageScore}%`, color: 'text-violet-600', bg: 'bg-violet-50' },
                { label: 'Passed',         value: statistics.passedCount,      color: 'text-emerald-600', bg: 'bg-emerald-50' },
                { label: 'Failed',         value: statistics.failedCount,      color: 'text-red-600',     bg: 'bg-red-50' },
              ].map(({ label, value, color, bg }) => (
                <div key={label} className={`${bg} rounded-xl border border-slate-100 p-4 text-center`}>
                  <p className={`text-2xl font-bold ${color}`}>{value}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          )}

          {responses.length === 0 ? (
            <div className="card p-12 text-center">
              <RiBarChartLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No responses yet</p>
              <p className="text-slate-400 text-sm mt-1">Students haven't submitted this test yet</p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Student</th>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Roll #</th>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Class</th>
                      <th className="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Score</th>
                      <th className="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Correct</th>
                      <th className="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Date</th>
                      <th className="px-5 py-3.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {responses.map(r => (
                      <tr key={r._id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <RiUserLine className="w-3.5 h-3.5 text-slate-500" />
                            </div>
                            <span className="font-semibold text-slate-800">{r.studentName}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 font-mono text-xs text-slate-600">{r.rollNumber}</td>
                        <td className="px-5 py-3.5 hidden sm:table-cell">
                          <span className="badge badge-slate">{r.className}</span>
                        </td>
                        <td className="px-5 py-3.5 text-center font-bold text-blue-600">{r.score}%</td>
                        <td className="px-5 py-3.5 text-center font-semibold text-emerald-600">
                          {r.correctAnswers}/{r.totalQuestions}
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white ${r.passed ? 'bg-emerald-500' : 'bg-red-500'}`}>
                            {r.passed
                              ? <><RiCheckLine className="w-3 h-3" /> Passed</>
                              : <><RiCloseLine className="w-3 h-3" /> Failed</>}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-slate-500 hidden md:table-cell">
                          {new Date(r.submittedAt).toLocaleDateString()}
                        </td>
                        <td className="px-5 py-3.5">
                          <button onClick={() => setSelectedResponse(r)}
                            className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors whitespace-nowrap">
                            Details →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TeacherResponses;
